import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSplash } from '../context/SplashContext';
import { useAuth } from '../context/AuthContext';
import deltaService from '../services/deltaService';

// The landing screen: the areas this portal holds, and nothing else.
//
// TILE FORMAT IS THE PARENT PLATFORM'S. Same chrome as Maghaweer's landing portals —
// rounded-2xl on a soft gradient, an emblem in a gold-bordered square, a confidential-grade
// status pill, a serif title — so the two products read as one house. Deliberately carried
// over: the cursor spotlight, the slight 3D tilt, the hairline along the top edge.
//
// Deliberately NOT carried over: the animated canvas texture behind each portal. Its motifs
// are a ticker tape and a signals mesh, which mean something on a trading platform and
// nothing on a governance register, and it costs an animation frame loop per tile.
//
// A TITLE AND A LINE. Earlier versions carried a paragraph and a row of chips on every
// tile; a landing screen is a choice between places, not the place itself, and the reader
// gets the explanation when they arrive. Each destination introduces itself on entry.

// Ring-fenced assets inside a bounded perimeter — the same emblem the parent platform uses
// for this module, so a user who knows one recognises the other.
const VAULT = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
       strokeLinecap="round" strokeLinejoin="round" width="26" height="26">
    <rect x="3" y="4.5" width="18" height="15" rx="2" />
    <circle cx="12" cy="12" r="3.6" /><circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
    <path d="M12 6.2v2.2M12 15.6v2.2M6.6 12h2.2M15.2 12h2.2" />
  </svg>
);

const CHART = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.1"
       strokeLinecap="round" strokeLinejoin="round" width="26" height="26">
    <path d="M4 19V5m0 14h16M4 15l4-4 3 2 6-7" />
    <circle cx="17" cy="6" r="1.4" fill="currentColor" stroke="none" />
  </svg>
);

// Candlesticks — the trading module's emblem, distinct from the dashboard's line CHART so the
// two figure-bearing areas read as related but not identical.
const CANDLES = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
       strokeLinecap="round" strokeLinejoin="round" width="26" height="26">
    <path d="M7 3v4M7 15v6M17 3v6M17 17v4" />
    <rect x="4.5" y="7" width="5" height="8" rx="1" />
    <rect x="14.5" y="9" width="5" height="8" rx="1" />
  </svg>
);

const QUILL = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
       strokeLinecap="round" strokeLinejoin="round" width="26" height="26">
    <path d="M4 20c0-6 4-11 10-13 2-.7 5-1 5-1s-.3 3-1 5c-2 6-7 10-13 10z" />
    <path d="M4 20l6-6" />
  </svg>
);

const SCOPE = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
       strokeLinecap="round" width="26" height="26">
    <circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="4" />
    <path d="M12 3.5v3M12 17.5v3M3.5 12h3M17.5 12h3" />
    <circle cx="12" cy="12" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);

const LEDGER = (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7"
       strokeLinecap="round" strokeLinejoin="round" width="26" height="26">
    <path d="M5 3.5h11l3.5 3.5v13.5H5z" /><path d="M15.5 3.5V7H19" />
    <path d="M8.5 12.5l2 2 4-4.5" />
  </svg>
);

export default function HomePage() {
  const { canWrite, canApprove } = useAuth();
  const nav = useNavigate();
  const { playSplash } = useSplash();
  const [queue, setQueue] = useState(null);

  // The splash carries the navigation rather than following it: `playSplash` fires the
  // callback mid-animation while the backdrop is still opaque, so the destination mounts
  // UNDERNEATH and the back-half crossfade reveals the new page. Navigating first would
  // show the old page for a frame before the splash covered it.
  //
  // Research OS gets the radar wheel — a sweep over concentric rings, which is what its own
  // emblem already is. Everywhere else gets the signal field.
  //
  // The Research OS tagline names both halves of what that area is — agentic reach and the
  // firm's own analysts — as one thing rather than as a handoff from the first to the second.
  // It MUST MATCH the Quorum tagline in pages/research/ResearchOSHome.js, which plays the same
  // splash one level down; the reasoning for the wording lives there.
  const enter = (t) => {
    const isResearch = t.key === 'research';
    playSplash(() => nav(t.to), isResearch ? 5200 : 3600, isResearch
      ? { spinner: true, tagline: 'Agentic AI and in-house intelligence.' }
      : { tagline: 'Signals in · Intelligence out', cap: true });
  };

  // The one live figure on this screen: how much is waiting on THIS person. Fetched only
  // for someone who can act on it — there is no point asking on behalf of a reader.
  useEffect(() => {
    if (!canWrite && !canApprove) return;
    deltaService.getMyQueue().then(setQueue).catch(() => setQueue(null));
  }, [canWrite, canApprove]);

  // Order is set by the owner and read top-left to bottom-right. Declared as one list in
  // that order rather than assembled by splice — a positional insert is how an order
  // someone chose gets quietly rearranged by the next edit.
  const tiles = [
    // Order is set by the owner and read top-left to bottom-right. Declared as one list in
    // that order rather than assembled by splice — a positional insert is how an order
    // someone chose gets quietly rearranged by the next edit.
    //
    // The dashboard runs on invented figures, so its status pill says so rather than
    // reporting "Online". The subtitle describes what the area IS; the pill describes what
    // state it is in. Keeping those separate is why the subtitle reads normally.
    { key: 'dashboard', to: '/dashboard', emblem: CHART, title: 'AUM Dashboard',
      sub: 'Performance and AUM summary', status: 'Sample data' },
    // Second, right after the dashboard. LIVE now: it reads Maghaweer's daily trading push,
    // so its pill reports "Online" like the other live areas — no status override. (The page
    // still falls back to illustrative with a banner if a push ever stops arriving; the tile
    // pill is not that signal. If the feed is retired, restore status: 'Sample data'.)
    { key: 'trading', to: '/trading', emblem: CANDLES, title: 'Automated Trading',
      sub: 'Performance and holdings' },
    { key: 'research', to: '/research', emblem: SCOPE, title: 'Research OS',
      // Must match the splash tagline in `enter()` below — the tile and the transition
      // out of it are read seconds apart, so two wordings read as two products.
      sub: 'Agentic AI and in-house intelligence' },
    { key: 'delta', to: '/delta', emblem: VAULT, title: 'Project Delta', sub: 'Reorganisation plan' },
    // One place for the framework and the work on it. Everyone may look; what you can DO to
    // a policy is decided per role by the API, not by hiding the area.
    {
      key: 'policies',
      to: '/policies',
      emblem: QUILL,
      title: 'Policy Pack',
      sub: queue?.awaiting_you
        ? `${queue.awaiting_you} waiting on you`
        : 'The framework, drafts and approval',
      badge: queue?.awaiting_you || 0,
    },
    // Last, and only for someone who can act on it.
    ...(canWrite
      ? [{ key: 'register', to: '/register', emblem: LEDGER, title: 'Asset Register', sub: 'Checking and sign-off' }]
      : []),
  ];

  // The track count follows the number of areas, so the row is always full. A fixed
  // three-column grid left a read-only user's two tiles hugging the left with a visibly
  // empty third cell — the layout should not advertise what this account cannot see.
  const cols = tiles.length >= 3 ? 'sm:grid-cols-2 lg:grid-cols-3' : 'sm:grid-cols-2';

  return (
    /* One container width for the pill and the tiles, so the pill's right edge lines up
       with the last tile's rather than floating past it. */
    <div className="mx-auto max-w-5xl">
      <div className="mb-6 flex items-center justify-end">
        <span className="rounded border border-red-300 px-2 py-1 font-mono text-[9px] uppercase tracking-[0.2em] text-red-500 dark:border-red-800 dark:text-red-400">
          Confidential
        </span>
      </div>

      <div className={`grid grid-cols-1 gap-4 ${cols}`} style={{ perspective: '1200px' }}>
        {tiles.map((t, i) => (
          <Portal key={t.key} {...t} delay={60 + i * 55}
                  onEnter={t.soon ? undefined : () => enter(t)} />
        ))}
      </div>
    </div>
  );
}

function Portal({ emblem, title, sub, onEnter, delay, badge, soon, status }) {
  const ref = useRef(null);

  // Tilt and spotlight follow the cursor. Both are pointer-only affordances — a touch
  // device simply never sets the variables and the card stays flat, which is correct.
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width;
    const y = (e.clientY - r.top) / r.height;
    el.style.setProperty('--ry', `${((x - 0.5) * 6).toFixed(2)}deg`);
    el.style.setProperty('--rx', `${((0.5 - y) * 5).toFixed(2)}deg`);
    el.style.setProperty('--mx', `${(x * 100).toFixed(1)}%`);
    el.style.setProperty('--my', `${(y * 100).toFixed(1)}%`);
  };
  const onLeave = () => {
    const el = ref.current;
    if (!el) return;
    el.style.setProperty('--rx', '0deg');
    el.style.setProperty('--ry', '0deg');
  };

  return (
    <button
      ref={ref}
      onClick={onEnter}
      disabled={!!soon}
      aria-disabled={soon ? 'true' : undefined}
      onMouseMove={soon ? undefined : onMove}
      onMouseLeave={soon ? undefined : onLeave}
      style={{
        transform: 'perspective(1200px) rotateX(var(--rx,0)) rotateY(var(--ry,0))',
        boxShadow: '0 22px 60px -44px rgba(0,0,0,.5)',
        animationDelay: `${delay}ms`,
      }}
      /* `relative` anchors the spotlight and hairline overlays; `isolate` keeps their
         negative z-index behind the content but inside this card. */
      className={`deck-rise group relative isolate flex min-h-[168px] flex-col justify-between overflow-hidden rounded-2xl border border-gray-200 bg-gradient-to-br from-white to-gray-50 p-5 text-left transition-all duration-300 dark:border-gray-800 dark:from-[#15171d] dark:to-[#111318] md:min-h-[204px] ${
        soon ? 'cursor-default opacity-60' : 'hover:border-brand-gold/50'}`}
    >
      <span className={`pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 ${
              soon ? '' : 'group-hover:opacity-100'}`}
            style={{ zIndex: -1, background: 'radial-gradient(300px 300px at var(--mx,50%) var(--my,40%), rgb(var(--brand-gold-rgb)/0.2), transparent 60%)' }} />
      <span className="pointer-events-none absolute left-0 right-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-gold/60 to-transparent opacity-50 transition-opacity group-hover:opacity-100" />

      <div className="flex items-center justify-between">
        <span className="grid h-12 w-12 place-items-center rounded-xl border border-brand-gold/40 bg-brand-gold/10 text-brand-gold">
          {emblem}
        </span>
        {soon ? (
          <span className="rounded-full border border-gray-300 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.15em] text-gray-400 dark:border-gray-700 dark:text-gray-500">
            Coming soon
          </span>
        ) : badge ? (
          /* A count only appears when something is actually waiting — a permanent "0" is
             noise, and a badge that is always there stops being read. */
          <span className="flex h-6 min-w-[24px] items-center justify-center rounded-full bg-brand-gold px-2 font-mono text-[11px] font-semibold text-white">
            {badge}
          </span>
        ) : (
          /* The indicator says whether the area is UP. For an area running on invented
             figures, "Online" is true and misleading in the same breath — it is the answer
             to a question nobody asked while the one that matters goes unanswered. Amber
             and named, so the state is legible before the tile is opened. */
          <span className={`flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.15em] ${
            status ? 'text-amber-600 dark:text-amber-500' : 'text-gray-400'}`}>
            <span className={`h-1.5 w-1.5 rounded-full ${status ? 'bg-amber-400' : 'bg-green-400'}`}
                  style={{ boxShadow: status ? '0 0 7px #fbbf24' : '0 0 7px #4ade80' }} />
            {status || 'Online'}
          </span>
        )}
      </div>

      <div>
        <h2 className="font-serif tracking-wide text-gray-900 dark:text-gray-100"
            style={{ fontSize: 'clamp(15px,1.5vw,19px)' }}>
          {title}
        </h2>
        <p className="mt-1 text-[11px] uppercase tracking-[1.5px] text-gray-400 dark:text-gray-500">
          {sub}
        </p>
      </div>
    </button>
  );
}
